import {
	buildAnchorPair,
	formatAnchorDetailLine,
	formatKkLabel,
	hasCompleteAnchor,
	shouldShowAnchor,
	toKkiMonthParam,
} from "@karama/shared";
import type { BundledKkiRate } from "./kki-bundled";
import { formatBundledMonthLabel } from "./kki-bundled";

export interface AnchorDisplayModel {
	state: "none" | "incomplete" | "complete";
	recordedKk: number | null;
	actualKk: number | null;
	showActual: boolean;
	driftPercent: number | null;
	pillLabel: string;
	detailLine: string;
	rateBanner: string | null;
}

export function buildAnchorDisplayModel(input: {
	anchorType: string;
	anchorKk: string | number | null;
	kkiVersion: string | null;
	country: string | null;
	amount: string | number | null;
	bundledRate: BundledKkiRate | null;
	precision?: number;
}): AnchorDisplayModel {
	const prec = input.precision ?? 1;

	if (!shouldShowAnchor({ anchorType: input.anchorType, anchorKkOrigin: null })) {
		return {
			state: "none",
			recordedKk: null,
			actualKk: null,
			showActual: false,
			driftPercent: null,
			pillLabel: "",
			detailLine: "",
			rateBanner: null,
		};
	}

	const recordedKk =
		input.anchorKk != null && input.anchorKk !== ""
			? Number.parseFloat(String(input.anchorKk))
			: null;

	if (
		!hasCompleteAnchor({
			anchorType: input.anchorType,
			anchorKkOrigin: recordedKk,
		})
	) {
		return {
			state: "incomplete",
			recordedKk: null,
			actualKk: null,
			showActual: false,
			driftPercent: null,
			pillLabel: "",
			detailLine: "",
			rateBanner: null,
		};
	}

	const amountNum =
		input.amount != null ? Number.parseFloat(String(input.amount)) : NaN;
	let actualKk: number | null = null;
	if (input.bundledRate && Number.isFinite(amountNum) && amountNum > 0) {
		actualKk = amountNum / input.bundledRate.kkValue;
	}

	const pair = buildAnchorPair({
		recordedKk,
		actualKk,
		precision: prec,
	});

	const displayKk = pair.showActual ? pair.actualRounded! : pair.recordedRounded!;
	const pillLabel = `${formatKkLabel(displayKk, prec)} · bundled`;

	const detailLine = `Anchored: ${formatAnchorDetailLine({
		kk: recordedKk!,
		kkiVersion: input.kkiVersion,
		country: input.country,
		precision: 2,
	}).replace(/^≈ /, "")}`;

	const rateBanner = input.bundledRate
		? `Bundled rate · as of ${formatBundledMonthLabel(input.bundledRate.month)}`
		: null;

	return {
		state: "complete",
		recordedKk,
		actualKk,
		showActual: pair.showActual,
		driftPercent: pair.driftPercent,
		pillLabel,
		detailLine,
		rateBanner,
	};
}

/** Preview model for correction form when amount/origin date changes recorded KK. */
export function buildPreviewAnchorDisplayModel(input: {
	recordedKk: number;
	previewKk: number;
	kkiVersion: string | null;
	country: string | null;
	bundledRate: BundledKkiRate | null;
	precision?: number;
}): AnchorDisplayModel {
	const prec = input.precision ?? 1;
	const pair = buildAnchorPair({
		recordedKk: input.recordedKk,
		actualKk: input.previewKk,
		precision: prec,
	});

	const displayKk = pair.showActual ? pair.actualRounded! : pair.recordedRounded!;
	const pillLabel = `${formatKkLabel(displayKk, prec)} · bundled`;

	const detailLine = `Anchored: ${formatAnchorDetailLine({
		kk: input.previewKk,
		kkiVersion: input.kkiVersion,
		country: input.country,
		precision: 2,
	}).replace(/^≈ /, "")}`;

	const rateBanner = input.bundledRate
		? `Bundled rate · as of ${formatBundledMonthLabel(input.bundledRate.month)}`
		: null;

	return {
		state: "complete",
		recordedKk: input.recordedKk,
		actualKk: input.previewKk,
		showActual: pair.showActual,
		driftPercent: pair.driftPercent,
		pillLabel,
		detailLine,
		rateBanner,
	};
}

export function buildCorrectionAnchorPreview(input: {
	recordedKk: number;
	amount: string;
	storedRate: number | null;
	bundledRate: BundledKkiRate | null;
	originDateIso: string;
}): { previewKk: number | null; driftPercent: number | null } {
	const amountNum = Number.parseFloat(input.amount);
	if (!Number.isFinite(amountNum) || amountNum <= 0) {
		return { previewKk: null, driftPercent: null };
	}

	let rate = input.storedRate;
	if (input.originDateIso && input.bundledRate) {
		// bundledRate is already resolved for origin month when caller passes it
		rate = input.bundledRate.kkValue;
	} else if (input.bundledRate) {
		rate = input.bundledRate.kkValue;
	}

	if (rate == null || rate <= 0) {
		return { previewKk: null, driftPercent: null };
	}

	const previewKk = amountNum / rate;
	const pair = buildAnchorPair({
		recordedKk: input.recordedKk,
		actualKk: previewKk,
		precision: 1,
	});

	return {
		previewKk,
		driftPercent: pair.driftPercent,
	};
}

export { toKkiMonthParam };
